
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { ProgressBar } from './ProgressBar';

interface MatchResultsCardProps {
  homeWinPercentage: string;
  drawPercentage: string;
  awayWinPercentage: string;
  popularScores?: Array<{
    score: string;
    count: number;
    percentage: string;
  }>;
  homeWinScores?: Array<{
    score: string;
    count: number;
    percentage: string;
  }>;
  awayWinScores?: Array<{
    score: string;
    count: number;
    percentage: string;
  }>;
  drawScores?: {
    all: Array<{
      score: string;
      count: number;
      percentage: string;
    }>;
    higher: Array<{
      score: string;
      count: number;
      percentage: string;
    }>;
    neutral: Array<{
      score: string;
      count: number;
      percentage: string;
    }>;
  };
}

export const MatchResultsCard: React.FC<MatchResultsCardProps> = ({
  homeWinPercentage,
  drawPercentage,
  awayWinPercentage,
  popularScores = [],
  homeWinScores = [],
  awayWinScores = [],
  drawScores = {
    all: [],
    higher: [],
    neutral: []
  }
}) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 mb-3">Match Results</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center gap-4">
                <span className="text-sm w-24">Home Win</span>
                <div className="flex-1">
                  <ProgressBar percentage={homeWinPercentage} color="bg-green-500" />
                </div>
                <span className="text-sm font-medium w-16 text-right">{homeWinPercentage}%</span>
              </div>
              <div className="flex justify-between items-center gap-4">
                <span className="text-sm w-24">Draw</span>
                <div className="flex-1">
                  <ProgressBar percentage={drawPercentage} color="bg-orange-400" />
                </div>
                <span className="text-sm font-medium w-16 text-right">{drawPercentage}%</span>
              </div>
              <div className="flex justify-between items-center gap-4">
                <span className="text-sm w-24">Away Win</span>
                <div className="flex-1">
                  <ProgressBar percentage={awayWinPercentage} color="bg-red-400" />
                </div>
                <span className="text-sm font-medium w-16 text-right">{awayWinPercentage}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {homeWinScores && homeWinScores.length > 0 && (
          <Card>
            <CardContent>
              <h4 className="text-sm font-semibold mb-3">Home Win Scores</h4>
              <div className="grid gap-2">
                {homeWinScores.map((score, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded py-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">{index + 1}.</span>
                      <span className="font-medium">{score.score}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">({score.count})</span>
                      <span className="text-sm font-medium text-gray-700">{score.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {drawScores.all.length > 0 && (
          <Card>
            <CardContent>
              <h4 className="text-sm font-semibold mb-3">Draw Scores</h4>
              <div className="grid gap-2">
                {drawScores.all.map((score, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded py-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">{index + 1}.</span>
                      <span className="font-medium">{score.score}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">({score.count})</span>
                      <span className="text-sm font-medium text-gray-700">{score.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {awayWinScores && awayWinScores.length > 0 && (
          <Card>
            <CardContent>
              <h4 className="text-sm font-semibold mb-3">Away Win Scores</h4>
              <div className="grid gap-2">
                {awayWinScores.map((score, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded py-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">{index + 1}.</span>
                      <span className="font-medium">{score.score}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">({score.count})</span>
                      <span className="text-sm font-medium text-gray-700">{score.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {popularScores && popularScores.length > 0 && (
          <Card>
            <CardContent>
              <h4 className="text-sm font-semibold mb-3">Top 5 Most Common Scores</h4>
              <div className="grid gap-2">
                {popularScores.map((score, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded py-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">{index + 1}.</span>
                      <span className="font-medium">{score.score}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">({score.count})</span>
                      <span className="text-sm font-medium text-gray-700">{score.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
